/**
 * pRI LLM Settings Page
 * Epic E-PRI-3: pRI LLM Beállítások Admin Felület
 *
 * 3-section admin page:
 * 1. API Konfiguráció (provider, API key, enabled, connection test)
 * 2. Rate Limiting & Kvóták (per-user, per-tenant, monthly quotas)
 * 3. Használati Statisztikák (read-only monthly aggregates)
 */

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
} from '@/components/ui';
import { useApiError } from '@/hooks/use-api-error';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@kgc/i18n';
import { ArrowLeft, Bot, Check, Eye, EyeOff, Loader2, Save, Wifi, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UpdatePriLlmConfigInput } from '../../api/pri-settings';
import {
  usePriSettings,
  usePriSettingsMutations,
  usePriUsageStats,
} from '../../hooks/use-pri-settings';

export function PriLlmSettingsPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { config, isLoading, error } = usePriSettings();
  const { update, test } = usePriSettingsMutations();
  const { usage, isLoading: usageLoading } = usePriUsageStats();

  // Form state
  const [form, setForm] = useState<UpdatePriLlmConfigInput>({});
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    model?: string;
    latencyMs?: number;
    error?: string;
  } | null>(null);

  // Initialize form from loaded config
  useEffect(() => {
    if (config) {
      setForm({
        provider: config.provider,
        enabled: config.enabled,
        userPerMinLimit: config.userPerMinLimit,
        tenantPerMinLimit: config.tenantPerMinLimit,
        userPerDayLimit: config.userPerDayLimit,
        sonnetPerMinLimit: config.sonnetPerMinLimit,
        monthlyQuotaBasic: config.monthlyQuotaBasic,
        monthlyQuotaPro: config.monthlyQuotaPro,
        monthlyQuotaEnterprise: config.monthlyQuotaEnterprise,
        defaultTier: config.defaultTier,
        autoDowngradeEnabled: config.autoDowngradeEnabled,
      });
    }
  }, [config]);

  const handleSave = useCallback(async () => {
    const payload: UpdatePriLlmConfigInput = { ...form };
    if (apiKeyInput) {
      payload.geminiApiKey = apiKeyInput;
    }

    try {
      await update.mutateAsync(payload);
      setApiKeyInput('');
      toast({ title: t('priLlm.saveSuccessTitle'), description: t('priLlm.saveSuccessDesc') });
    } catch (err) {
      toast({
        title: t('priLlm.saveErrorTitle'),
        description: getErrorMessage(err, t('priLlm.unknownError')),
        variant: 'destructive',
      });
    }
  }, [form, apiKeyInput, update, toast]);

  const handleTest = useCallback(async () => {
    setTestResult(null);
    try {
      const res = await test.mutateAsync();
      setTestResult(res.data);
    } catch (err) {
      setTestResult({
        ok: false,
        error: getErrorMessage(err, t('priLlm.networkError')),
      });
    }
  }, [test]);

  const updateField = <K extends keyof UpdatePriLlmConfigInput>(
    key: K,
    value: UpdatePriLlmConfigInput[K]
  ) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin-center')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2">
          <Bot className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">{t('priLlm.title')}</h1>
        </div>
      </div>

      {/* Section 1: API Konfiguráció */}
      <Card>
        <CardHeader>
          <CardTitle>{t('priLlm.apiConfigTitle')}</CardTitle>
          <CardDescription>{t('priLlm.apiConfigDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Provider */}
          <div className="space-y-2">
            <label htmlFor="provider" className="text-sm font-medium">
              {t('priLlm.providerLabel')}
            </label>
            <select
              id="provider"
              value={form.provider ?? 'gemini-flash'}
              onChange={e => updateField('provider', e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="gemini-flash">Gemini 2.5 Flash</option>
              <option value="gemini-pro">Gemini 2.5 Pro</option>
            </select>
          </div>

          {/* API Key */}
          <div className="space-y-2">
            <label htmlFor="apiKey" className="text-sm font-medium">
              {t('priLlm.apiKeyLabel')}
            </label>
            {config?.geminiApiKeyMasked && !apiKeyInput && (
              <p className="text-sm text-muted-foreground">
                {t('priLlm.currentKeyLabel')}{' '}
                <code className="rounded bg-gray-100 px-1 dark:bg-gray-800">
                  {config.geminiApiKeyMasked}
                </code>
              </p>
            )}
            <div className="relative">
              <Input
                id="apiKey"
                type={showApiKey ? 'text' : 'password'}
                placeholder={t('priLlm.apiKeyPlaceholder')}
                value={apiKeyInput}
                onChange={e => setApiKeyInput(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
              >
                {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Enabled Toggle */}
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <span className="text-sm font-medium">{t('priLlm.enabledLabel')}</span>
              <p className="text-sm text-muted-foreground">{t('priLlm.enabledDesc')}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.enabled ?? false}
              onClick={() => updateField('enabled', !(form.enabled ?? false))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form.enabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  form.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Connection Test */}
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleTest} disabled={test.isPending}>
              {test.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Wifi className="mr-2 h-4 w-4" />
              )}
              {t('priLlm.testConnection')}
            </Button>
            {testResult && (
              <div className="flex items-center gap-2 text-sm">
                {testResult.ok ? (
                  <>
                    <Check className="h-4 w-4 text-green-500" />
                    <span className="text-green-600">
                      {t('priLlm.testOk', {
                        model: testResult.model ?? '',
                        latencyMs: testResult.latencyMs ?? 0,
                      })}
                    </span>
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4 text-red-500" />
                    <span className="text-red-600">
                      {testResult.error ?? t('priLlm.testFailed')}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Section 2: Rate Limiting & Kvóták */}
      <Card>
        <CardHeader>
          <CardTitle>{t('priLlm.rateLimitTitle')}</CardTitle>
          <CardDescription>{t('priLlm.rateLimitDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <NumberField
              label={t('priLlm.userPerMin')}
              id="userPerMin"
              value={form.userPerMinLimit ?? 30}
              onChange={v => updateField('userPerMinLimit', v)}
            />
            <NumberField
              label={t('priLlm.tenantPerMin')}
              id="tenantPerMin"
              value={form.tenantPerMinLimit ?? 300}
              onChange={v => updateField('tenantPerMinLimit', v)}
            />
            <NumberField
              label={t('priLlm.userPerDay')}
              id="userPerDay"
              value={form.userPerDayLimit ?? 500}
              onChange={v => updateField('userPerDayLimit', v)}
            />
            <NumberField
              label={t('priLlm.sonnetPerMin')}
              id="sonnetPerMin"
              value={form.sonnetPerMinLimit ?? 5}
              onChange={v => updateField('sonnetPerMinLimit', v)}
            />
          </div>

          <div className="border-t pt-4">
            <h4 className="mb-3 text-sm font-medium">{t('priLlm.monthlyQuotasTitle')}</h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <NumberField
                label="Basic"
                id="quotaBasic"
                value={form.monthlyQuotaBasic ?? 500}
                onChange={v => updateField('monthlyQuotaBasic', v)}
              />
              <NumberField
                label="Pro"
                id="quotaPro"
                value={form.monthlyQuotaPro ?? 2000}
                onChange={v => updateField('monthlyQuotaPro', v)}
              />
              <NumberField
                label="Enterprise"
                id="quotaEnterprise"
                value={form.monthlyQuotaEnterprise ?? 10000}
                onChange={v => updateField('monthlyQuotaEnterprise', v)}
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <h4 className="mb-3 text-sm font-medium">{t('priLlm.tierStrategyTitle')}</h4>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="defaultTier" className="text-sm font-medium">
                  {t('priLlm.defaultTierLabel')}
                </label>
                <select
                  id="defaultTier"
                  value={form.defaultTier ?? 'flash'}
                  onChange={e => updateField('defaultTier', e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="flash">Flash</option>
                  <option value="pro">Pro</option>
                  <option value="sonnet">Sonnet</option>
                </select>
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <span className="text-sm font-medium">{t('priLlm.autoDowngradeLabel')}</span>
                  <p className="text-xs text-muted-foreground">{t('priLlm.autoDowngradeDesc')}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.autoDowngradeEnabled ?? true}
                  onClick={() =>
                    updateField('autoDowngradeEnabled', !(form.autoDowngradeEnabled ?? true))
                  }
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    (form.autoDowngradeEnabled ?? true)
                      ? 'bg-primary'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      (form.autoDowngradeEnabled ?? true) ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Használati Statisztikák */}
      <Card>
        <CardHeader>
          <CardTitle>{t('priLlm.usageStatsTitle')}</CardTitle>
          <CardDescription>{t('priLlm.usageStatsDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {usageLoading ? (
            <div className="flex h-20 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : usage ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <StatCard label={t('priLlm.requests')} value={String(usage.totalRequests)} />
              <StatCard
                label={t('priLlm.inputTokens')}
                value={formatNumber(usage.totalInputTokens)}
              />
              <StatCard
                label={t('priLlm.outputTokens')}
                value={formatNumber(usage.totalOutputTokens)}
              />
              <StatCard
                label={t('priLlm.costUsd')}
                value={`$${(usage.totalCostUsd ?? 0).toFixed(2)}`}
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{t('priLlm.noUsageData')}</p>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={update.isPending} size="lg">
          {update.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          {t('priLlm.saveButton')}
        </Button>
      </div>
    </div>
  );
}

// ============================================
// Helper Components
// ============================================

function NumberField({
  label,
  id,
  value,
  onChange,
}: {
  label: string;
  id: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="text-sm font-medium">
        {label}
      </label>
      <Input
        id={id}
        type="number"
        min={0}
        value={value}
        onChange={e => onChange(Math.max(1, parseInt(e.target.value, 10) || 1))}
      />
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-3 text-center">
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
